/**
 * Client helpers for QR voting remote (meeting rooms + vote sessions).
 *
 * QR / participant lane — keep separate from officer sync:
 * - Voters: anonymous `/r/:joinCode` + device_token (this module).
 * - Officers: authenticated `meeting_sessions` via officerSession.js (Realtime).
 * Do not require login for scan-and-vote.
 */

import { getSupabaseClient } from '../lib/supabaseClient';

const DEVICE_TOKEN_KEY = 'pp_vote_device_token';
const DISPLAY_NAME_KEY = 'pp_vote_display_name';

export function getOrCreateDeviceToken() {
  try {
    let token = localStorage.getItem(DEVICE_TOKEN_KEY);
    if (!token || token.length < 8) {
      token =
        (typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`) + '';
      localStorage.setItem(DEVICE_TOKEN_KEY, token);
    }
    return token;
  } catch {
    return `ephemeral-${Date.now()}`;
  }
}

export function getStoredDisplayName() {
  try {
    return localStorage.getItem(DISPLAY_NAME_KEY) || '';
  } catch {
    return '';
  }
}

export function setStoredDisplayName(name) {
  try {
    const trimmed = String(name || '').trim();
    if (trimmed) localStorage.setItem(DISPLAY_NAME_KEY, trimmed);
    else localStorage.removeItem(DISPLAY_NAME_KEY);
  } catch {
    /* ignore */
  }
}

export function remoteJoinUrl(joinCode, origin = window.location.origin) {
  const code = String(joinCode || '').toUpperCase();
  return `${origin}/r/${code}`;
}

export async function ensureMeetingRoom(organizationId, meetingId = null) {
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase.rpc('ensure_meeting_room', {
    p_organization_id: organizationId,
    p_meeting_id: meetingId || null,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row?.join_code && !row?.joinCode) {
    throw new Error('Failed to ensure meeting room');
  }
  return {
    roomId: row.room_id || row.roomId,
    joinCode: row.join_code || row.joinCode,
    meetingId: row.meeting_id || row.meetingId || null,
    isActive: row.is_active ?? row.isActive ?? true,
  };
}

export async function joinMeetingRoom(joinCode) {
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase.rpc('join_meeting_room', {
    p_join_code: String(joinCode || '').toUpperCase(),
  });
  if (error) throw error;
  return data;
}

export async function openVoteSession({
  roomId,
  kind,
  questionText,
  options = ['aye', 'no', 'abstain'],
  clientRef = null,
}) {
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase.rpc('open_vote_session', {
    p_room_id: roomId,
    p_kind: kind,
    p_question_text: questionText,
    p_options: options,
    p_client_ref: clientRef,
  });
  if (error) throw error;
  return data;
}

export async function closeVoteSession(sessionId) {
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase.rpc('close_vote_session', {
    p_session_id: sessionId,
  });
  if (error) throw error;
  return data;
}

export async function castVoteBallot({ sessionId, deviceToken, choice, displayName, writeInText }) {
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase.rpc('cast_vote_ballot', {
    p_session_id: sessionId,
    p_device_token: deviceToken,
    p_choice: choice,
    p_display_name: displayName || null,
    p_write_in_text: writeInText || null,
  });
  if (error) throw error;
  return data;
}

export async function getVoteSessionTally(sessionId) {
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase.rpc('get_vote_session_tally', {
    p_session_id: sessionId,
  });
  if (error) throw error;
  return data;
}

/** Map motion-style tally counts { aye, no, abstain } to { yes, no, abstain }. */
export function motionTallyFromCounts(counts = {}) {
  return {
    yes: Number(counts.aye || counts.yes || 0),
    no: Number(counts.no || 0),
    abstain: Number(counts.abstain || 0),
  };
}

export function electionVotesFromCounts(counts = {}, nominees = []) {
  const next = {};
  nominees.forEach((n) => {
    const id = String(n.id);
    next[id] = Number(counts[id] || 0);
  });
  return next;
}
